import React, {useState, useEffect, useCallback, useRef} from "react";
import {
    CreditCard, Receipt, Search, User, Phone, Mail, Package,
    Calendar, Clock, Tag, ArrowLeft, Banknote, Smartphone,
    Wallet, DollarSign, ChevronRight, RefreshCw, UtensilsCrossed, ShoppingBag,
    FileText, Loader2, BookOpen, Send, X, Truck, MapPin, Monitor, ChevronDown,
} from "lucide-react";
import {useTranslation} from "react-i18next";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {toLocalDateStr} from "../../utils/dateHelpers";
import moment from "moment";
import toast from "react-hot-toast";
import RegisterSelect from "./components/RegisterSelect";
import Button from "../Common/Button";
import {SearchInput} from "../../stock/StockUI";
import {Group, Row, RoundAction, rowDivider} from "./components/GroupedList";

// Moment's locale is synced globally by useSyncMomentLocale() at the app root.

const Ventes = ({_dateRange = [], _startTime, _endTime, refreshKey = 0, isDarkMode = false}) => {
    const {t, i18n} = useTranslation("activite");
    const [salesData, setSalesData] = useState([]);
    // Days folded in the list (dayData.date)
    const [collapsedDays, setCollapsedDays] = useState(() => new Set());
    const toggleDay = (day) => setCollapsedDays((current) => {
        const next = new Set(current);
        next.has(day) ? next.delete(day) : next.add(day);
        return next;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
    const [isSendingInvoiceEmail, setIsSendingInvoiceEmail] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [highlightedItem, setHighlightedItem] = useState(null);
    const activeRowRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    // Filtre par caisse ('' = toutes) — voir components/RegisterSelect.js
    const [register, setRegister] = useState('');
    const [isRestaurant, setIsRestaurant] = useState(false);
    const [showInvoiceEmailModal, setShowInvoiceEmailModal] = useState(false);
    const [invoiceEmailForm, setInvoiceEmailForm] = useState({email: '', message: ''});

    // Check once if this is a restaurant account (busniessType === "004")
    useEffect(() => {
        secureStorage.getItem('selectedAccount').then(acc => {
            setIsRestaurant(acc?.busniessType === '004');
        });
    }, []);

    // ── Data fetching ────────────────────────────────────────────────────────
    const loadSalesData = useCallback(async (silent = false) => {
        if (!_dateRange?.[0]?.startDate || !_dateRange?.[0]?.endDate) {
            if (!silent) setIsLoading(false);
            return;
        }
        if (silent) setIsRefreshing(true); else setIsLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            if (!accountId?.id) {
                if (silent) setIsRefreshing(false); else setIsLoading(false);
                return;
            }
            const start = toLocalDateStr(_dateRange[0].startDate);
            const end = toLocalDateStr(_dateRange[0].endDate);
            const startTimeFormatted = _startTime || "00:00";
            const endTimeFormatted = _endTime || "23:59";
            const registerParam = register ? `&registers=${encodeURIComponent(register)}` : '';
            const res = await api.get(
                `/activite/sales?startDate=${start}&endDate=${end}&startTime=${startTimeFormatted}&endTime=${endTimeFormatted}${registerParam}`,
                {headers: {"X-account-id": accountId.id, "Accept": "application/json"}}
            );
            if (res.data?.data) setSalesData(res.data.data);
        } catch (e) {
            console.error("Error fetching sales:", e);
        } finally {
            if (silent) setIsRefreshing(false); else setIsLoading(false);
        }
    }, [_dateRange, _startTime, _endTime, register]);

    useEffect(() => {
        loadSalesData(false);
    }, [loadSalesData, refreshKey]);

    // Keep the selected row visible inside the sidebar's own scroll area
    useEffect(() => {
        if (highlightedItem && activeRowRef.current) {
            activeRowRef.current.scrollIntoView({block: 'nearest'});
        }
    }, [highlightedItem]);

    const fetchSaleDetails = async (saleId) => {
        setIsLoadingDetails(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            if (!accountId?.id) {
                setIsLoadingDetails(false);
                return;
            }
            const res = await api.get(`/activite/sales/${saleId}`, {
                headers: {"X-account-id": accountId.id, "Accept": "application/json"}
            });
            if (res.data) setSelectedItemDetails(res.data);
        } catch (e) {
            console.error("Error fetching details:", e);
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const handleRowClick = (item) => {
        if (highlightedItem === item.id) {
            setHighlightedItem(null);
            setSelectedItem(null);
            setSelectedItemDetails(null);
        } else {
            setHighlightedItem(item.id);
            setSelectedItem(item);
            fetchSaleDetails(item.id);
        }
    };

    const handleBack = () => {
        setSelectedItem(null);
        setSelectedItemDetails(null);
        setHighlightedItem(null);
        setShowInvoiceEmailModal(false);
        setInvoiceEmailForm({email: '', message: ''});
    };

    // ── Invoice generation ───────────────────────────────────────────────────────
    const generateInvoice = async (saleId) => {
        setIsGeneratingInvoice(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            if (!accountId?.id) {
                toast.error(t("ventes.accountNotSelected"));
                return;
            }
            const res = await api.get(`/generate_invoice/${saleId}`, {
                headers: {
                    "X-account-id": accountId.id,
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                }
            });
            if (res.data?.status === "OK" && res.data?.url) {
                // Open PDF in new tab
                window.open(res.data.url, '_blank');
                toast.success(t("ventes.invoiceGenerated", {number: res.data.invoice_number}));
            } else {
                toast.error(t("ventes.invoiceGenerationError"));
            }
        } catch (e) {
            console.error("Error generating invoice:", e);
            toast.error(e.response?.data?.message || t("ventes.invoiceGenerationError"));
        } finally {
            setIsGeneratingInvoice(false);
        }
    };

    const openInvoiceEmailModal = useCallback(() => {
        setInvoiceEmailForm({
            email: selectedItemDetails?.client?.email || '',
            message: '',
        });
        setShowInvoiceEmailModal(true);
    }, [selectedItemDetails]);

    const closeInvoiceEmailModal = useCallback((force = false) => {
        if (isSendingInvoiceEmail && !force) return;
        setShowInvoiceEmailModal(false);
        setInvoiceEmailForm({email: '', message: ''});
    }, [isSendingInvoiceEmail]);

    const sendInvoiceByEmail = async () => {
        const email = invoiceEmailForm.email.trim();
        const message = invoiceEmailForm.message.trim();

        if (!selectedItem?.id) {
            toast.error(t("ventes.saleNotFound"));
            return;
        }

        if (!email) {
            toast.error(t("ventes.recipientEmailRequired"));
            return;
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(email)) {
            toast.error(t("ventes.invalidEmail"));
            return;
        }

        setIsSendingInvoiceEmail(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            if (!accountId?.id) {
                toast.error(t("ventes.accountNotSelected"));
                return;
            }

            const payload = {email};
            if (message) payload.message = message;

            const res = await api.post(`/send_invoice/${selectedItem.id}`, payload, {
                headers: {
                    "X-account-id": accountId.id,
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                }
            });

            if (res.data?.status === 'OK') {
                toast.success(t("ventes.invoiceSent", {number: res.data.invoice_number, recipient: res.data.recipient || email}));
                closeInvoiceEmailModal(true);
            } else {
                toast.error(t("ventes.invoiceSendError"));
            }
        } catch (e) {
            console.error("Error sending invoice by email:", e);
            toast.error(e.response?.data?.message || t("ventes.invoiceSendError"));
        } finally {
            setIsSendingInvoiceEmail(false);
        }
    };

    // ── Helpers ───────────────────────────────────────────────────────────────

    /**
     * Calculate the real monetary discount amount from a discount object + its product.
     * `discount.amount` on this API equals the *percentage value* (20), not money.
     * So we compute: base × rate / 100 for %, or flat value otherwise.
     */
    const calcDiscountMoney = (discount, product) => {
        if (!discount) return 0;
        const base = product ? (product.price || 0) * (product.quantity || 1) : 0;
        if (discount.type === '%') return base > 0 ? base * discount.value / 100 : discount.value;
        return discount.value || 0;
    };

    /** The API sometimes returns a single `discount` object on a product instead of a `discounts` array. */
    const getProductDiscounts = (product) => {
        if (!product) return [];
        if (Array.isArray(product.discounts)) return product.discounts;
        if (product.discount) return [product.discount];
        return [];
    };

    /** Return false when the note is the literal string "undefined" / null / empty */
    const validNote = (note) => note && note.trim() !== '' && note.toLowerCase() !== 'undefined';

    const getDateStr = (v) => (!v ? null : typeof v === 'object' && v.date ? v.date : v);

    const formatTime = (v) => {
        const s = getDateStr(v);
        if (!s) return "";
        const d = moment(s, "YYYY-MM-DD HH:mm:ss");
        return d.isValid() ? d.format('HH:mm') : "";
    };

    const formatDate = (v, fmt = 'DD/MM/YYYY') => {
        const s = getDateStr(v);
        if (!s) return "";
        const d = moment(s, "YYYY-MM-DD HH:mm:ss");
        return d.isValid() ? d.format(fmt) : "";
    };

    const formatDateHeader = (str) => {
        if (!str) return "";
        // Locale-aware weekday/month names via moment, whose locale is synced
        // globally by useSyncMomentLocale() — replaces a hand-rolled French-only
        // day/month name lookup that ignored the active language.
        const [d, m, y] = str.split('/').map(Number);
        return moment(new Date(y, m - 1, d)).format('dddd D MMM YYYY');
    };

    const filterSales = (sales) => {
        if (!searchTerm) return sales;
        const q = searchTerm.toLowerCase();
        return sales.filter(s =>
            s.id.toLowerCase().includes(q) || (s.note && s.note.toLowerCase().includes(q))
        );
    };

    const statusColor = (s) => {
        switch (s?.toLowerCase()) {
            case 'completed':
            case 'terminé':
            case 'terminer':
                return 'bg-green-500';
            case 'pending':
            case 'en attente':
                return 'bg-yellow-500';
            case 'cancelled':
            case 'annulé':
            case 'annulée':
                return 'bg-red-500';
            case 'refund':
            case 'remboursé':
            case 'remboursée':
                return 'bg-rose-600';
            default:
                return 'bg-primary';
        }
    };

    /** Human-readable, translated label for a sale's raw API status. */
    const statusLabel = (s) => {
        switch (s?.toLowerCase()) {
            case 'completed':
            case 'terminé':
            case 'terminer':
                return t("ventes.completedStatus");
            case 'pending':
            case 'en attente':
                return t("ventes.statusPending");
            case 'cancelled':
            case 'annulé':
            case 'annulée':
                return t("ventes.statusCancelled");
            case 'refund':
            case 'remboursé':
            case 'remboursée':
                return t("ventes.statusRefund");
            default:
                return s || t("ventes.completedStatus");
        }
    };

    /** Refunded/cancelled sales carry a negative total and get red/rose styling instead of the default primary teal. */
    const isReversalStatus = (s) => ['cancelled', 'annulé', 'annulée', 'refund', 'remboursé', 'remboursée'].includes(s?.toLowerCase());

    /** Retours & échanges: status stays 'terminer', sale_type tells them apart. */
    const returnTypeLabel = (sale) => {
        if (sale?.sale_type === 'return') return t("ventes.typeReturn");
        if (sale?.sale_type === 'exchange') return t("ventes.typeExchange");
        return null;
    };

    /** Red styling for money going back to the customer. */
    const isNegativeSale = (sale) =>
        isReversalStatus(sale?.status) || (returnTypeLabel(sale) !== null && Number(sale?.total) < 0);

    const hasReversalBadge = (sale) => isReversalStatus(sale?.status) || returnTypeLabel(sale) !== null;
    const reversalBadgeLabel = (sale) => returnTypeLabel(sale) || statusLabel(sale?.status);

    const reversalBadgeClass = isDarkMode
        ? 'bg-rose-900/30 text-rose-400 border border-rose-800'
        : 'bg-rose-50 text-rose-600 border border-rose-200';

    const paymentIcon = (name) => {
        const p = {className: "w-5 h-5"};
        switch (name?.toLowerCase()) {
            case 'creditcard':
            case 'carte bancaire':
            case 'card':
                return <CreditCard {...p}/>;
            case 'cash':
            case 'espece':
            case 'espèce':
            case 'banknote':
            case 'bank':
                return <Banknote {...p}/>;
            case 'mobile':
            case 'smartphone':
            case 'phone':
                return <Smartphone {...p}/>;
            case 'wallet':
            case 'portefeuille':
                return <Wallet {...p}/>;
            case 'dollar':
            case 'money':
                return <DollarSign {...p}/>;
            default:
                return <CreditCard {...p}/>;
        }
    };

    const pageBgClass = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
    const panelClass = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    const headerClass = isDarkMode ? 'bg-gray-900/70 border-gray-700' : 'bg-gray-50 border-gray-100';
    const inputClass = isDarkMode
        ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500'
        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400';
    const textPrimaryClass = isDarkMode ? 'text-gray-100' : 'text-gray-900';
    const textSecondaryClass = isDarkMode ? 'text-gray-300' : 'text-gray-700';
    const textMutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const rowDividerClass = isDarkMode ? 'border-gray-700' : 'border-gray-100';
    const subtleDividerClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';
    const hoverRowClass = isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50';
    const activeRowClass = isDarkMode ? 'bg-primary/15 border-l-4 border-primary' : 'bg-blue-50 border-l-4 border-primary';
    const chipClass = isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700';

    // ── Loading ───────────────────────────────────────────────────────────────
    if (isLoading) return (
        <div
            className={`min-h-[400px] border-y flex items-center justify-center py-24 ${panelClass}`}>
            <div className="text-center">
                <div
                    className="w-12 h-12 border-4 border-blue-200 border-t-primary rounded-full animate-spin mx-auto mb-4"/>
                <p className={`text-sm ${textMutedClass}`}>{t("ventes.loading")}</p>
            </div>
        </div>
    );

    const InvoiceEmailModal = () => {
        if (!showInvoiceEmailModal || !selectedItem) return null;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={closeInvoiceEmailModal}>
                <div
                    className={`w-full max-w-lg rounded-2xl shadow-2xl border overflow-hidden ${panelClass}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={`flex items-center justify-between px-5 py-4 border-b ${rowDividerClass}`}>
                        <div>
                            <h3 className={`text-lg font-semibold ${textPrimaryClass}`}>{t("ventes.sendInvoiceByEmail")}</h3>
                            <p className={`text-sm mt-0.5 ${textMutedClass}`}>{t("ventes.saleNumber", {id: selectedItem.id.slice(0, 8)})}</p>
                        </div>
                        <button
                            onClick={closeInvoiceEmailModal}
                            disabled={isSendingInvoiceEmail}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        >
                            <X className={`w-5 h-5 ${textMutedClass}`}/>
                        </button>
                    </div>

                    <div className="px-5 py-4 space-y-4">
                        <div>
                            <label className={`block text-sm font-medium mb-1.5 ${textSecondaryClass}`}>{t("ventes.recipientEmail")}</label>
                            <input
                                type="email"
                                value={invoiceEmailForm.email}
                                onChange={(e) => setInvoiceEmailForm(prev => ({...prev, email: e.target.value}))}
                                placeholder="client@example.com"
                                className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${inputClass}`}
                            />
                            {selectedItemDetails?.client?.email && (
                                <p className={`mt-1.5 text-xs flex items-center gap-1 ${textMutedClass}`}>
                                    <Mail className="w-3.5 h-3.5"/>
                                    {t("ventes.clientEmailDetected", {email: selectedItemDetails.client.email})}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className={`block text-sm font-medium mb-1.5 ${textSecondaryClass}`}>{t("ventes.customMessage")}</label>
                            <textarea
                                rows={5}
                                value={invoiceEmailForm.message}
                                onChange={(e) => setInvoiceEmailForm(prev => ({...prev, message: e.target.value}))}
                                placeholder={t("ventes.customMessagePlaceholder")}
                                className={`w-full rounded-lg border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary ${inputClass}`}
                            />
                            <p className={`mt-1.5 text-xs ${textMutedClass}`}>
                                {t("ventes.leaveEmptyForDefault")}
                            </p>
                        </div>
                    </div>

                    <div className={`flex items-center justify-end gap-3 px-5 py-4 border-t ${headerClass}`}>
                        <button
                            onClick={closeInvoiceEmailModal}
                            disabled={isSendingInvoiceEmail}
                            className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 ${isDarkMode ? 'text-gray-200 border-gray-700 hover:bg-gray-700' : 'text-gray-700 border-gray-200 hover:bg-gray-100'}`}
                        >
                            {t("ventes.cancel")}
                        </button>
                        <button
                            onClick={sendInvoiceByEmail}
                            disabled={isSendingInvoiceEmail}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSendingInvoiceEmail ? (
                                <Loader2 className="w-4 h-4 animate-spin"/>
                            ) : (
                                <Send className="w-4 h-4"/>
                            )}
                            {isSendingInvoiceEmail ? t("ventes.sending") : t("ventes.send")}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Shared: Sale Detail Content ───────────────────────────────────────────
    // Same layout as the POS sale detail (SaleDetailView): header with the
    // number + status, round actions, then grouped lists — edge to edge on
    // phones, rounded cards from md up.
    const DetailContent = () => {
        const orderType = selectedItemDetails?.order_type === 'delivery'
            ? {icon: Truck, label: t("ventes.deliveryOrder")}
            : isRestaurant && (selectedItemDetails?.table
                ? {icon: UtensilsCrossed, label: t("ventes.dineIn"), value: `${selectedItemDetails.table.name} · ${t("ventes.seatsCount", {count: selectedItemDetails.table.capacity})}`}
                : {icon: ShoppingBag, label: t("ventes.takeawayOrder")});
        const iconBadge = (Icon) => (
            <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4"/></span>
        );
        return (
        <div className="pb-10 w-full">
            {/* Header: back, number + status */}
            <div className="px-4 pt-4">
                <Button variant="plain" size="sm" icon={ArrowLeft} className="-ml-2 mb-1" onClick={handleBack}>
                    {t("ventes.back")}
                </Button>
                <div className="flex items-center justify-between gap-3">
                    <h2 className="text-[28px] leading-tight font-bold tracking-tight text-gray-900 dark:text-gray-100 truncate">
                        {t("ventes.saleNumber", {id: selectedItem.id.slice(0, 8)})}
                    </h2>
                    <span className="flex items-center gap-2 flex-shrink-0">
                        {returnTypeLabel(selectedItem) && (
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${reversalBadgeClass}`}>
                                {returnTypeLabel(selectedItem)}
                            </span>
                        )}
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium text-white ${statusColor(selectedItem.status)}`}>
                            {statusLabel(selectedItem.status)}
                        </span>
                    </span>
                </div>
                <p className="mt-0.5 text-[15px] text-gray-500 dark:text-gray-400">
                    {t("ventes.dateAtTime", {date: formatDate(selectedItem.createdAt), time: formatTime(selectedItem.createdAt)})}
                    {selectedItemDetails?.register_name ? ` · ${selectedItemDetails.register_name}` : ''}
                </p>
            </div>

            {/* Hero amount */}
            <div className="flex flex-col items-center pt-5">
                <p className={`text-4xl sm:text-[40px] font-bold tracking-tight tabular-nums ${isNegativeSale(selectedItem) ? 'text-rose-600' : 'text-gray-900 dark:text-gray-100'}`}>
                    {selectedItem.total ? selectedItem.total.toFixed(2) : "0.00"} DH
                </p>
                <p className="text-[15px] text-gray-500 dark:text-gray-400 mt-0.5">{t("ventes.itemsCount", {count: selectedItem.itemCount})}</p>
            </div>

            {/* Actions — round buttons, as on the POS */}
            <div className="flex justify-center gap-2 mt-5 px-4">
                <RoundAction icon={FileText} label={isGeneratingInvoice ? t("ventes.generating") : t("ventes.invoice")}
                             loading={isGeneratingInvoice} onClick={() => generateInvoice(selectedItem.id)}/>
                <RoundAction icon={Send} label={isSendingInvoiceEmail ? t("ventes.sendingShort") : t("ventes.sendEmail")}
                             loading={isSendingInvoiceEmail} disabled={isLoadingDetails} onClick={openInvoiceEmailModal}/>
            </div>

            {isLoadingDetails && (
                <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 text-primary animate-spin mb-3"/>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("ventes.loadingDetails")}</p>
                </div>
            )}

            {selectedItemDetails && !isLoadingDetails && (
                <>
                    {/* Informations */}
                    <Group title={t("ventes.information")}>
                        {orderType && (
                            <Row first leading={iconBadge(orderType.icon)} label={orderType.label}
                                 value={orderType.value ?? (selectedItemDetails.order_type === 'delivery' && selectedItemDetails.delivery?.fee != null ? `${Number(selectedItemDetails.delivery.fee).toFixed(2)} DH` : undefined)}/>
                        )}
                        {selectedItemDetails.register_name && (
                            <Row first={!orderType} leading={iconBadge(Monitor)} label={t("registers.column")} value={selectedItemDetails.register_name}/>
                        )}
                        {selectedItemDetails.sales_channel && (
                            <Row first={!orderType && !selectedItemDetails.register_name}
                                 leading={selectedItemDetails.sales_channel.image_url
                                     ? <img src={selectedItemDetails.sales_channel.image_url} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0"
                                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}/>
                                     : iconBadge(ShoppingBag)}
                                 label={t("ventes.salesChannel")}
                                 sub={selectedItemDetails.sales_channel.description}
                                 value={selectedItemDetails.sales_channel.name}/>
                        )}
                        <Row first={!orderType && !selectedItemDetails.register_name && !selectedItemDetails.sales_channel} leading={iconBadge(User)}
                             label={t("ventes.createdBy")} value={selectedItemDetails.created_employee?.name || t("ventes.notAvailable")}/>
                        {selectedItemDetails.updated_employee && (
                            <Row leading={iconBadge(User)} label={t("ventes.modifiedBy")} value={selectedItemDetails.updated_employee.name}/>
                        )}
                        <Row leading={iconBadge(Smartphone)} label={t("ventes.device")} value={selectedItemDetails.device_created || t("ventes.notAvailable")}/>
                    </Group>

                    {/* Client */}
                    {selectedItemDetails.client && (
                        <Group title={t("ventes.client")}>
                            <Row first strong label={`${selectedItemDetails.client.first_name || ''} ${selectedItemDetails.client.last_name || ''}`.trim() || t("ventes.notAvailable")}/>
                            {selectedItemDetails.client.phone && <Row leading={iconBadge(Phone)} label={selectedItemDetails.client.phone}/>}
                            {selectedItemDetails.client.email && <Row leading={iconBadge(Mail)} label={selectedItemDetails.client.email}/>}
                        </Group>
                    )}

                    {/* Livraison */}
                    {selectedItemDetails.order_type === 'delivery' && selectedItemDetails.delivery && (
                        <Group title={t("ventes.deliveryInfo")}>
                            {selectedItemDetails.delivery.name && <Row first strong label={selectedItemDetails.delivery.name}/>}
                            {selectedItemDetails.delivery.phone && <Row first={!selectedItemDetails.delivery.name} leading={iconBadge(Phone)} label={selectedItemDetails.delivery.phone}/>}
                            {selectedItemDetails.delivery.address && <Row first={!selectedItemDetails.delivery.name && !selectedItemDetails.delivery.phone} leading={iconBadge(MapPin)} label={selectedItemDetails.delivery.address}/>}
                            {!selectedItemDetails.delivery.name && !selectedItemDetails.delivery.phone && !selectedItemDetails.delivery.address && (
                                <Row first label={t("ventes.notAvailable")}/>
                            )}
                            {selectedItemDetails.delivery.fee != null && (
                                <Row label={t("ventes.deliveryFee")} value={`${Number(selectedItemDetails.delivery.fee).toFixed(2)} DH`}/>
                            )}
                        </Group>
                    )}

                    {/* Articles */}
                    <Group title={t("ventes.articles", {count: selectedItemDetails.products?.length || 0})}>
                            {selectedItemDetails.products?.map((product, i) =>
                                product.is_menu ? (
                                    /* ── Menu entry ── */
                                    <div key={i} className={`py-3 pr-4 ${rowDivider(i === 0)}`}>
                                        <div className="flex items-center justify-between mb-2.5">
                                            <div className="flex items-center gap-2.5">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-teal-900/30' : 'bg-teal-100'}`}>
                                                    <BookOpen className={`w-4 h-4 ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}/>
                                                </div>
                                                <div>
                                                    <p className={`font-semibold text-sm ${isDarkMode ? 'text-teal-300' : 'text-teal-800'}`}>{product.menu_name}</p>
                                                    <p className={`text-xs ${textMutedClass}`}>
                                                        {isRestaurant ? t("ventes.menu") : t("ventes.pack")} · {t("ventes.itemsCount", {count: product.menu_items?.length || 0})}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className={`font-semibold text-sm whitespace-nowrap ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                                                {product.total?.toFixed(2)} DH
                                            </p>
                                        </div>
                                                <div className="ml-10 space-y-1.5">
                                            {product.menu_items?.map((item, ii) => (
                                                <div key={ii} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 ${isDarkMode ? 'bg-gray-900/70' : 'bg-gray-50'}`}>
                                                    {item.image ? (
                                                        <img src={item.image} alt={item.name}
                                                             className={`w-8 h-8 rounded object-cover border flex-shrink-0 ${subtleDividerClass}`}
                                                             onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                                            <Package className={`w-4 h-4 ${textMutedClass}`}/>
                                                        </div>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            {item.group && (
                                                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium capitalize ${isDarkMode ? 'bg-teal-900/30 text-teal-400' : 'bg-teal-100 text-teal-700'}`}>
                                                                    {item.group}
                                                                </span>
                                                            )}
                                                            <p className={`text-xs font-medium truncate ${textSecondaryClass}`}>{item.name}</p>
                                                        </div>
                                                        <p className={`text-xs ${textMutedClass}`}>{item.price?.toFixed(2)} DH × {item.quantity}</p>
                                                    </div>
                                                    <p className={`text-xs font-semibold whitespace-nowrap flex-shrink-0 ${textSecondaryClass}`}>
                                                        {item.total?.toFixed(2)} DH
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    /* ── Regular product ── */
                                    <div key={i} className={`py-3 pr-4 flex items-center justify-between gap-3 ${rowDivider(i === 0)}`}>
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            {product.image ? (
                                                <img src={product.image} alt={product.name}
                                                     className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover border flex-shrink-0 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                                                     onError={(e) => {
                                                         e.target.onerror = null;
                                                         e.target.style.display = 'none';
                                                     }}
                                                />
                                            ) : (
                                                        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                                            <Package className={`w-6 h-6 ${textMutedClass}`}/>
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                        <p className={`font-medium truncate text-sm ${textPrimaryClass}`}>{product.name}</p>
                                                        <p className={`text-xs ${textMutedClass}`}>{product.price.toFixed(2)} DH × {product.quantity}</p>
                                                {product.is_return && (
                                                    <div className="flex flex-wrap items-center gap-1 mt-1">
                                                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${reversalBadgeClass}`}>
                                                            {t("ventes.itemReturned")}
                                                        </span>
                                                        {product.return_reason && (
                                                            <span className={`text-xs ${textMutedClass}`}>{product.return_reason}</span>
                                                        )}
                                                        <span className={`text-xs ${textMutedClass}`}>
                                                            · {product.restock ? t("ventes.itemRestocked") : t("ventes.itemNotRestocked")}
                                                        </span>
                                                    </div>
                                                )}
                                                {product.employee && (
                                                    <div className="flex items-center gap-1 mt-1">
                                                        <User className="w-3 h-3 text-primary flex-shrink-0"/>
                                                        <span className="text-xs text-primary font-medium truncate">{product.employee.name}</span>
                                                    </div>
                                                )}
                                                {getProductDiscounts(product).length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                        {getProductDiscounts(product).map((d, di) => {
                                                            const money = calcDiscountMoney(d, product);
                                                            return (
                                                                <span key={di} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-100 text-red-600'}`}>
                                                                    <Tag className="w-2.5 h-2.5 flex-shrink-0"/>
                                                                    {d.name}{' '}
                                                                    {d.type === '%'
                                                                        ? t("ventes.discountPercent", {percent: d.value, amount: money.toFixed(2)})
                                                                        : t("ventes.discountFlat", {amount: money.toFixed(2)})}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            {getProductDiscounts(product).length > 0 ? (
                                                <>
                                                    <p className={`font-semibold text-sm whitespace-nowrap ${textPrimaryClass}`}>
                                                        {(product.total - getProductDiscounts(product).reduce((s, d) => s + calcDiscountMoney(d, product), 0)).toFixed(2)} DH
                                                    </p>
                                                    <p className={`text-xs line-through whitespace-nowrap ${textMutedClass}`}>
                                                        {product.total.toFixed(2)} DH
                                                    </p>
                                                </>
                                            ) : (
                                                <p className={`font-semibold text-sm whitespace-nowrap ${textPrimaryClass}`}>{product.total.toFixed(2)} DH</p>
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        
                    </Group>

                    {/* Paiements */}
                    <Group title={t("ventes.payments", {count: selectedItemDetails.payments?.length || 0})}>
                        {selectedItemDetails.payments?.length ? selectedItemDetails.payments.map((payment, i) => (
                            <Row key={i} first={i === 0}
                                 leading={<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4">{paymentIcon(payment.icon)}</span>}
                                 label={payment.name} sub={payment.note}
                                 value={`${payment.amount.toFixed(2)} DH`} valueClass="font-semibold text-gray-900 dark:text-gray-100"/>
                        )) : <Row first label={t("ventes.notAvailable")}/>}
                    </Group>

                    {/* Remises */}
                    {selectedItemDetails.discounts?.length > 0 && (
                        <Group title={t("ventes.discounts", {count: selectedItemDetails.discounts.length})}>
                            {selectedItemDetails.discounts.map((disc, i) => {
                                const prod = selectedItemDetails.products?.find(p => p.id === disc.product_id);
                                const money = calcDiscountMoney(disc, prod);
                                return (
                                    <Row key={i} first={i === 0}
                                         leading={<span className="w-7 h-7 rounded-lg bg-red-500/10 text-red-600 flex items-center justify-center flex-shrink-0"><Tag className="w-4 h-4"/></span>}
                                         label={disc.type === "%" ? `${disc.name} (${disc.value}%)` : disc.name}
                                         sub={[disc.product_name && t("ventes.onProduct", {name: disc.product_name}), validNote(disc.note) && disc.note].filter(Boolean).join(" · ") || undefined}
                                         value={`−${money.toFixed(2)} DH`} valueClass="text-red-600"/>
                                );
                            })}
                        </Group>
                    )}

                    {/* Totaux */}
                    <Group title={t("ventes.summary")}>
                        {selectedItemDetails.total_ht !== undefined && (
                            <Row first label={t("ventes.totalHt")} value={`${selectedItemDetails.total_ht.toFixed(2)} DH`}/>
                        )}
                        {selectedItemDetails.total_remise > 0 && (
                            <Row label={t("ventes.discount")} value={`−${selectedItemDetails.total_remise.toFixed(2)} DH`} valueClass="text-red-600"/>
                        )}
                        {selectedItemDetails.delivery?.fee != null && (
                            <Row label={t("ventes.deliveryFee")} value={`${Number(selectedItemDetails.delivery.fee).toFixed(2)} DH`}/>
                        )}
                        <Row first={selectedItemDetails.total_ht === undefined && !(selectedItemDetails.total_remise > 0) && selectedItemDetails.delivery?.fee == null} strong
                             label={t("ventes.totalTtc")}
                             value={`${selectedItemDetails.total ? selectedItemDetails.total.toFixed(2) : "0.00"} DH`} valueClass="font-bold text-primary text-lg"/>
                    </Group>
                </>
            )}
        </div>
        );
    };

    // ── Shared: Sales list rows ───────────────────────────────────────────────
    const SaleRows = ({compact = false}) => (
        <>
            {salesData.length === 0 ? (
                <div className="text-center py-16">
                    <Receipt className={`w-14 h-14 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}/>
                    <h3 className={`text-base font-medium mb-1 ${textPrimaryClass}`}>{t("ventes.noSalesFound")}</h3>
                    <p className={`text-sm ${textMutedClass}`}>{t("ventes.noSalesForPeriod")}</p>
                </div>
            ) : salesData.map((dayData, di) => (
                <div key={di} className={`border-b last:border-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-50'}`}>
                    <button
                        type="button"
                        onClick={() => toggleDay(dayData.date)}
                        aria-expanded={!collapsedDays.has(dayData.date)}
                        className={`w-full flex items-center gap-2 px-4 py-2 border-b text-left transition-colors ${headerClass} ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${textMutedClass} ${collapsedDays.has(dayData.date) ? '-rotate-90' : ''}`}/>
                        <p className={`flex-1 text-xs font-semibold uppercase tracking-wide ${textMutedClass}`}>{formatDateHeader(dayData.date)}</p>
                        <span className={`text-xs ${textMutedClass}`}>{filterSales(dayData.sales).length}</span>
                    </button>
                    {!collapsedDays.has(dayData.date) && <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-50'}`}>
                        {filterSales(dayData.sales).map((sale, si) => (
                            <div
                                key={si}
                                ref={highlightedItem === sale.id ? activeRowRef : null}
                                onClick={() => handleRowClick(sale)}
                                className={`px-4 py-3.5 cursor-pointer transition-all duration-150 ${
                                    highlightedItem === sale.id ? activeRowClass : hoverRowClass
                                }`}
                            >
                                {compact ? (
                                    /* Desktop sidebar compact row */
                                    <div className="flex justify-between items-center gap-2">
                                        <div className="min-w-0">
                                            <p className={`font-semibold text-sm ${isNegativeSale(sale) ? 'text-rose-600' : textPrimaryClass}`}>{sale.total ? sale.total.toFixed(2) : "0.00"} DH</p>
                                            <p className={`text-xs mt-0.5 ${textMutedClass}`}>#{sale.id.slice(0, 8)} • {formatTime(sale.createdAt)}</p>
                                        </div>
                                        {hasReversalBadge(sale) ? (
                                            <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${reversalBadgeClass}`}>
                                                {reversalBadgeLabel(sale)}
                                            </span>
                                        ) : (
                                            <div
                                                className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor(sale.status)}`}/>
                                        )}
                                    </div>
                                ) : (
                                    /* Full row — stacks on mobile */
                                    <div
                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-4">
                                        {/* Left: status dot + amount + count */}
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusColor(sale.status)}`}/>
                                            <div className="flex items-center gap-1.5">
                                                <Receipt className={`w-4 h-4 flex-shrink-0 ${isNegativeSale(sale) ? 'text-rose-500' : 'text-primary'}`}/>
                                                <span className={`font-semibold text-base sm:text-lg ${isNegativeSale(sale) ? 'text-rose-600' : textPrimaryClass}`}>
                                                    {sale.total ? sale.total.toFixed(2) : "0.00"} DH
                                                </span>
                                            </div>
                                            <div className={`flex items-center gap-1 ${textMutedClass}`}>
                                                <Package className="w-3.5 h-3.5 flex-shrink-0"/>
                                                <span className="text-xs">{t("ventes.itemsAbbr", {count: sale.itemCount})}</span>
                                            </div>
                                            {hasReversalBadge(sale) && (
                                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${reversalBadgeClass}`}>
                                                    {reversalBadgeLabel(sale)}
                                                </span>
                                            )}
                                        </div>
                                        {/* Right: note + id + time + chevron (mobile) */}
                                        <div
                                            className={`flex items-center justify-between sm:justify-end gap-3 pl-6 sm:pl-0 text-xs ${textMutedClass}`}>
                                            <span className="truncate max-w-[140px] sm:max-w-xs">
                                                {sale.note || t("ventes.standardSale")}
                                            </span>
                                            <span
                                                className={`font-mono px-1.5 py-0.5 rounded hidden sm:inline ${chipClass}`}>
                                                #{sale.id.slice(0, 8)}
                                            </span>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <Clock className="w-3 h-3"/>{formatTime(sale.createdAt)}
                                            </div>
                                            <ChevronRight className="w-4 h-4 sm:hidden flex-shrink-0 text-gray-400"/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>}
                </div>
            ))}
        </>
    );

    // ── Detail selected ───────────────────────────────────────────────────────
    if (selectedItem) {
        return (
            <>
                {/* ── Mobile: full-screen detail ── */}
                <div className={`md:hidden min-h-screen overflow-y-auto ${pageBgClass}`}>
                    <DetailContent/>
                </div>

                {/* ── Desktop: side-by-side ── */}
                <div
                    className={`hidden md:flex sticky top-0 h-[calc(100vh-180px)] min-h-[500px] overflow-hidden border-y ${pageBgClass} ${subtleDividerClass}`}>
                    {/* Sidebar — stays in place, its own list scrolls internally */}
                    <div className={`w-72 lg:w-80 xl:w-96 border-r flex flex-col flex-shrink-0 h-full overflow-hidden ${panelClass}`}>
                        <div className={`p-3 border-b ${rowDividerClass}`}>
                            <div className="relative">
                                <Search className={`w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 ${textMutedClass}`}/>
                                <input
                                    type="text"
                                    placeholder={t("ventes.searchShort")}
                                    className={`w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${inputClass}`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto scrollbar-hide" style={{scrollbarWidth: 'none'}}>
                            <SaleRows compact={true}/>
                        </div>
                    </div>
                    {/* Detail pane */}
                    <div className="flex-1 overflow-y-auto scrollbar-hide" style={{scrollbarWidth: 'none'}}>
                        <DetailContent/>
                    </div>
                </div>

                <InvoiceEmailModal/>
            </>
        );
    }

    // ── List view (no selection) ──────────────────────────────────────────────
    return (
        // Pleine largeur comme les listes Stock / catalogue : pas d'arrondi ni d'ombre
        <div className={`border-b overflow-hidden ${panelClass}`}>
            {/* Toolbar — recherche, caisse, période + actualiser (style Stock / Retours) */}
            <div className="flex flex-col sm:flex-row">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t("ventes.searchPlaceholder")}/>
                {/* Negative margins: neighbouring cells share one hairline instead of doubling it */}
                <div className="flex -mt-px sm:mt-0 sm:-ml-px">
                    <RegisterSelect value={register} onChange={setRegister} className="flex-1 sm:w-64"/>
                    <div className="-ml-px flex items-center gap-3 px-3 sm:px-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                        <span className={`hidden lg:flex items-center gap-1.5 text-sm whitespace-nowrap ${textMutedClass}`}>
                            <Calendar className="w-4 h-4 flex-shrink-0"/>
                            {_dateRange?.[0]
                                ? `${_dateRange[0].startDate?.toLocaleDateString(i18n.language)} – ${_dateRange[0].endDate?.toLocaleDateString(i18n.language)}`
                                : t("ventes.allDates")}
                        </span>
                        <Button variant="plain" size="sm" icon={RefreshCw} loading={isRefreshing} compact
                                onClick={() => loadSalesData(true)}>
                            {t("ventes.refresh")}
                        </Button>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="max-h-[600px] overflow-y-auto scrollbar-hide" style={{scrollbarWidth: 'none'}}>
                <SaleRows compact={false}/>
            </div>
        </div>
    );
};

export default Ventes;
